import { Suspense, lazy } from 'react';
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import GlobalSEO from "./components/seo/GlobalSEO";
import ScrollToTop from "./components/ScrollToTop";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ChunkErrorBoundary } from "./components/ChunkErrorBoundary";
import { SellerErrorBoundary } from "./components/SellerErrorBoundary";
import BottomNavBar from "./components/BottomNavBar";
// NOTE: UserProvider moved to main.tsx to ensure it wraps everything including error boundaries
import { ProfileProvider } from "./contexts/ProfileContext";

// Lightweight loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

// ============================================
// LAZY LOADED PAGES - Critical for bundle size
// ============================================

// Core public pages (loaded first in priority)
const Index = lazy(() => import("./pages/Index"));
const Catalog = lazy(() => import("./pages/Catalog"));
const Product = lazy(() => import("./pages/Product"));
const Company = lazy(() => import("./pages/Company"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));

// Auth pages
const Auth = lazy(() => import("./pages/Auth"));
const PhoneAuth = lazy(() => import("./pages/PhoneAuth"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const VerifyEmailPending = lazy(() => import("./pages/VerifyEmailPending"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Browse pages
const Categories = lazy(() => import("./pages/Categories"));
const Explore = lazy(() => import("./pages/Explore"));
const ExploreRegion = lazy(() => import("./pages/ExploreRegion"));
const Sellers = lazy(() => import("./pages/Sellers"));
const Wishlist = lazy(() => import("./pages/Wishlist"));

// Orders & Payment
const Orders = lazy(() => import("./pages/Orders"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const PaymentStatus = lazy(() => import("./pages/PaymentStatus"));
const CardcomSuccess = lazy(() => import("./pages/CardcomSuccess"));
const CardcomFailure = lazy(() => import("./pages/CardcomFailure"));
const CardComSuccess = lazy(() => import("./pages/checkout/CardComSuccess"));
const CardComCancel = lazy(() => import("./pages/checkout/CardComCancel"));
const CardcomError = lazy(() => import("./pages/CardcomError"));

// Bundles
const BundlesCatalog = lazy(() => import("./pages/BundlesCatalog"));
const PublicCollaborationBundle = lazy(() => import("./pages/PublicCollaborationBundle"));

// Stories / Blog
const LocalStories = lazy(() => import("./pages/LocalStories"));
const StoryDetail = lazy(() => import("./pages/StoryDetail"));
const Blog = lazy(() => import("./pages/Blog"));
const ShopStoriesList = lazy(() => import("./pages/ShopStoriesList"));
const ShopStoryPage = lazy(() => import("./pages/ShopStoryPage"));

// Blog posts
const StoryOfIsraeliCrafts = lazy(() => import("./pages/blog/StoryOfIsraeliCrafts"));
const TopGiftsFromIsrael = lazy(() => import("./pages/blog/TopGiftsFromIsrael"));
const MeetTheArtisans = lazy(() => import("./pages/blog/MeetTheArtisans"));
const IsraeliCraftsCulture = lazy(() => import("./pages/blog/IsraeliCraftsCulture"));
const HandmadeVsMassProduced = lazy(() => import("./pages/blog/HandmadeVsMassProduced"));

// Static pages
const About = lazy(() => import("./pages/About"));
const AboutArtisans = lazy(() => import("./pages/AboutArtisans"));
const Mission = lazy(() => import("./pages/Mission"));
const Press = lazy(() => import("./pages/Press"));
const HowWeVerify = lazy(() => import("./pages/HowWeVerify"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const ShippingPolicy = lazy(() => import("./pages/ShippingPolicy"));
const SellerVerification = lazy(() => import("./pages/SellerVerification"));
const CustomsInfo = lazy(() => import("./pages/CustomsInfo"));
const Returns = lazy(() => import("./pages/Returns"));
const DataDeletion = lazy(() => import("./pages/DataDeletion"));
const DataProtection = lazy(() => import("./pages/legal/DataProtection"));
const CookiesPolicy = lazy(() => import("./pages/legal/Cookies"));
const PrivacyCenter = lazy(() => import("./pages/PrivacyCenter"));
const LegalConsent = lazy(() => import("./pages/LegalConsent"));
const Referrals = lazy(() => import("./pages/Referrals"));
const AboutLocalCraft = lazy(() => import("./pages/AboutLocalCraft"));

// Support
const Support = lazy(() => import("./pages/Support"));
const Tickets = lazy(() => import("./pages/Tickets"));
const TicketDetail = lazy(() => import("./pages/TicketDetail"));
const BuyerChats = lazy(() => import("./pages/BuyerChats"));
const NotificationCenter = lazy(() => import("./pages/NotificationCenter"));

// Travel / Stays
const StaysHomepage = lazy(() => import("./pages/stays/index"));
const StaysSearch = lazy(() => import("./pages/stays/search"));
const StayDetails = lazy(() => import("./pages/stays/[slug]"));

// PWA / Utility
const Offline = lazy(() => import("./pages/Offline"));
const Install = lazy(() => import("./pages/Install"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Seller Onboarding
const SellerOnboarding = lazy(() => import("./pages/SellerOnboarding"));

// ============================================
// SELLER DASHBOARD - Heavy, lazy load entire section
// ============================================
const Dashboard = lazy(() => import("./pages/Dashboard"));
const EditCompany = lazy(() => import("./pages/EditCompany"));
const DashboardProducts = lazy(() => import("./pages/DashboardProducts"));
const ProductForm = lazy(() => import("./pages/ProductForm"));
const DashboardOrders = lazy(() => import("./pages/DashboardOrders"));
const DashboardCRM = lazy(() => import("./pages/DashboardCRM"));
const DashboardLeads = lazy(() => import("./pages/DashboardLeads"));
const CustomerDetail = lazy(() => import("./pages/CustomerDetail"));
const DashboardChats = lazy(() => import("./pages/DashboardChats"));
const DashboardTickets = lazy(() => import("./pages/DashboardTickets"));
const DashboardSettings = lazy(() => import("./pages/DashboardSettings"));
const DashboardReturns = lazy(() => import("./pages/DashboardReturns"));
const DashboardDelivery = lazy(() => import("./pages/DashboardDelivery"));
const DashboardDeliveryAnalytics = lazy(() => import("./pages/DashboardDeliveryAnalytics"));
const DashboardAuditLogs = lazy(() => import("./pages/DashboardAuditLogs"));
const DashboardManagers = lazy(() => import("./pages/DashboardManagers"));
const ShopStories = lazy(() => import("./pages/seller/ShopStories"));
const ShopStoryEditor = lazy(() => import("./pages/seller/ShopStoryEditor"));
const ImportProducts = lazy(() => import("./pages/ImportProducts"));

// Seller routes
const SellerChats = lazy(() => import("./pages/SellerChats"));
const SellerNotifications = lazy(() => import("./pages/SellerNotifications"));
const SellerWallet = lazy(() => import("./pages/SellerWallet"));
const SellerFinancial = lazy(() => import("./pages/SellerFinancial"));
const SellerCRM = lazy(() => import("./pages/SellerCRM"));
const SellerAIAssistant = lazy(() => import("./pages/SellerAIAssistant"));
const SellerPromos = lazy(() => import("./pages/SellerPromos"));
const SellerProfile = lazy(() => import("./pages/SellerProfile"));
const SellerAnalytics = lazy(() => import("./pages/SellerAnalytics"));
const SellerInventory = lazy(() => import("./pages/SellerInventory"));
const SellerBundles = lazy(() => import("./pages/SellerBundles"));
const SellerBundleForm = lazy(() => import("./pages/SellerBundleForm"));
const SellerCollaborationBundles = lazy(() => import("./pages/SellerCollaborationBundles"));
const SellerCollaborationBundleForm = lazy(() => import("./pages/SellerCollaborationBundleForm"));
const BundleInvitations = lazy(() => import("./pages/seller/BundleInvitations"));
const RespondToInvitation = lazy(() => import("./pages/seller/RespondToInvitation"));
const BundleWizard = lazy(() => import("./pages/seller/BundleWizard"));
const SellerBundlesAnalyticsPage = lazy(() => import("./pages/seller/analytics/SellerBundlesAnalyticsPage"));
const SellerInvitesPage = lazy(() => import("./pages/seller/bundles/SellerInvitesPage"));
const ContributeToBundlePage = lazy(() => import("./pages/seller/bundles/ContributeToBundlePage"));
const OfferBuilderPage = lazy(() => import("./pages/seller/bundles/OfferBuilderPage"));

// ============================================
// ADMIN - Very heavy, definitely lazy load
// ============================================
const Admin = lazy(() => import("./pages/Admin"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminSellers = lazy(() => import("./pages/AdminSellers"));
const AdminPartners = lazy(() => import("./pages/AdminPartners"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminCategories = lazy(() => import("./pages/AdminCategories"));
const AdminRegions = lazy(() => import("./pages/AdminRegions"));
const AdminStories = lazy(() => import("./pages/AdminStories"));
const ScrapeProductImages = lazy(() => import("./pages/ScrapeProductImages"));
const ImportShopProducts = lazy(() => import("./pages/ImportShopProducts"));
const AdminWhatsAppTesting = lazy(() => import("./pages/AdminWhatsAppTesting"));
const AdminWhatsAppSettings = lazy(() => import("./pages/AdminWhatsAppSettings"));
const AdminTravel = lazy(() => import("./pages/admin/Travel"));
const AdminTravelProperties = lazy(() => import("./pages/admin/travel/Properties"));
const AdminTravelRoomsRates = lazy(() => import("./pages/admin/travel/RoomsRates"));
const AdminTravelBookings = lazy(() => import("./pages/admin/travel/Bookings"));
const AdminTravelProviders = lazy(() => import("./pages/admin/travel/Providers"));
const AdminModeration = lazy(() => import("./pages/AdminModeration"));
const AdminTranslations = lazy(() => import("./pages/AdminTranslations"));
const AdminReturns = lazy(() => import("./pages/AdminReturns"));
const AdminDisputes = lazy(() => import("./pages/AdminDisputes"));
const AdminFraud = lazy(() => import("./pages/AdminFraud"));
const AdminOCR = lazy(() => import("./pages/AdminOCR"));
const AdminPayouts = lazy(() => import("./pages/AdminPayouts"));
const AdminFinancial = lazy(() => import("./pages/AdminFinancial"));
const AdminOrders = lazy(() => import("./pages/AdminOrders"));
const AdminOrderDetail = lazy(() => import("./pages/AdminOrderDetail"));
const AdminProducts = lazy(() => import("./pages/AdminProducts"));
const AdminReviews = lazy(() => import("./pages/AdminReviews"));
const AdminCollaborationBundles = lazy(() => import("./pages/AdminCollaborationBundles"));
const AdminCollaborationBundleDetail = lazy(() => import("./pages/AdminCollaborationBundleDetail"));
const AdminSecurity = lazy(() => import("./pages/AdminSecurity"));
const AdminSelfAudit = lazy(() => import("./pages/AdminSelfAudit"));
const AdminTickets = lazy(() => import("./pages/AdminTickets"));
const AdminAuditLogs = lazy(() => import("./pages/AdminAuditLogs"));
const AdminMarketing = lazy(() => import("./pages/AdminMarketing"));
const AdminBanners = lazy(() => import("./pages/AdminBanners"));
const AdminSearchShortcuts = lazy(() => import("./pages/AdminSearchShortcuts"));
const CardComSettings = lazy(() => import("./pages/admin/CardComSettings"));
const PaymentsStatus = lazy(() => import("./pages/admin/PaymentsStatus"));
const PerShopImporter = lazy(() => import("./pages/PerShopImporter"));
const AdminShopRatings = lazy(() => import("./pages/AdminShopRatings"));
const AdminBundlesAnalyticsPage = lazy(() => import("./pages/admin/analytics/AdminBundlesAnalyticsPage"));
const AdminImageProcessing = lazy(() => import("./pages/AdminImageProcessing"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));

// User Account
const UserDashboard = lazy(() => import("./pages/UserDashboard"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const UserAddresses = lazy(() => import("./pages/UserAddresses"));
const UserNotifications = lazy(() => import("./pages/UserNotifications"));
const UserPaymentMethods = lazy(() => import("./pages/UserPaymentMethods"));

// Internal tools
const SeoDomainCheck = lazy(() => import("./pages/internal/SeoDomainCheck"));

// Layouts - keep synchronous for consistent UI shell
import { AdminLayout } from "./layouts/AdminLayout";
import { SellerLayout } from "./layouts/SellerLayout";
import { UserAccountLayout } from "./layouts/UserAccountLayout";

// Simple redirect component for legacy /products/:slug route
const ProductsRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/product/${slug}`} replace />;
};

const queryClient = new QueryClient();

/**
 * Main App Component
 * 
 * NOTE: UserProvider is now in main.tsx (top of tree) to ensure all components,
 * including error boundaries, have access to auth context.
 * 
 * Provider order here:
 * - HelmetProvider (SEO - can be anywhere)
 * - QueryClientProvider (data fetching)
 * - ProfileProvider (user profile data)
 * - TooltipProvider (UI)
 * - BrowserRouter (routing)
 */
const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ProfileProvider>
          <TooltipProvider>
            <GlobalSEO />
            
            <BrowserRouter>
              <ScrollToTop />
              <Sonner />
              <ChunkErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
              {/* Core public routes */}
              <Route path="/" element={<ErrorBoundary><Index /></ErrorBoundary>} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/verify-email" element={<VerifyEmail />} />
              <Route path="/auth/verify-email-pending" element={<VerifyEmailPending />} />
              <Route path="/auth/forgot-password" element={<ForgotPassword />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              <Route path="/phone-auth" element={<PhoneAuth />} />
              
              {/* Blog */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/story-of-israeli-handmade-crafts" element={<StoryOfIsraeliCrafts />} />
              <Route path="/blog/top-gifts-from-israel" element={<TopGiftsFromIsrael />} />
              <Route path="/blog/meet-the-artisans" element={<MeetTheArtisans />} />
              <Route path="/blog/israeli-crafts-culture" element={<IsraeliCraftsCulture />} />
              <Route path="/blog/handmade-vs-mass-produced" element={<HandmadeVsMassProduced />} />
              
              {/* About pages */}
              <Route path="/about-artisans" element={<AboutArtisans />} />
              <Route path="/mission" element={<Mission />} />
              <Route path="/press" element={<Press />} />
              <Route path="/how-we-verify" element={<HowWeVerify />} />
              
              {/* Browse */}
              <Route path="/catalog" element={<Catalog />} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/explore" element={<Explore />} />
              <Route path="/explore/:slug" element={<ExploreRegion />} />
              <Route path="/local-stories" element={<LocalStories />} />
              <Route path="/stories" element={<LocalStories />} />
              <Route path="/local-stories/:slug" element={<StoryDetail />} />
              <Route path="/stories/:slug" element={<StoryDetail />} />
              <Route path="/sellers" element={<Sellers />} />
              
              {/* Product & Company */}
              <Route path="/cart" element={<Cart />} />
              <Route path="/company/:companySlug/product/:slug" element={<Product />} />
              <Route path="/product/:slug" element={<Product />} />
              {/* Legacy redirect: /products/:slug → /product/:slug */}
              <Route path="/products/:slug" element={<ProductsRedirect />} />
              <Route path="/company/:slug" element={<Company />} />
              
              {/* Seller Onboarding */}
              <Route path="/seller-onboarding" element={<SellerOnboarding />} />
              
              {/* Seller Dashboard Routes with Layout */}
              <Route path="/dashboard" element={<SellerLayout><Dashboard /></SellerLayout>} />
              <Route path="/dashboard/company/edit" element={<SellerLayout><EditCompany /></SellerLayout>} />
              <Route path="/edit-company" element={<Navigate to="/dashboard/company/edit" replace />} />
              <Route path="/dashboard/products" element={<SellerLayout><DashboardProducts /></SellerLayout>} />
              <Route path="/dashboard/products/new" element={<SellerLayout><ProductForm /></SellerLayout>} />
              <Route path="/dashboard/products/edit/:id" element={<SellerLayout><ProductForm /></SellerLayout>} />
              <Route path="/dashboard/orders" element={<SellerLayout><DashboardOrders /></SellerLayout>} />
              <Route path="/dashboard/delivery" element={<SellerLayout><DashboardDelivery /></SellerLayout>} />
              <Route path="/dashboard/delivery-analytics" element={<SellerLayout><DashboardDeliveryAnalytics /></SellerLayout>} />
              <Route path="/dashboard/crm" element={<SellerLayout><DashboardCRM /></SellerLayout>} />
              <Route path="/dashboard/leads" element={<SellerLayout><DashboardLeads /></SellerLayout>} />
              <Route path="/dashboard/crm/customers/:id" element={<SellerLayout><CustomerDetail /></SellerLayout>} />
              <Route path="/dashboard/settings" element={<SellerLayout><DashboardSettings /></SellerLayout>} />
              <Route path="/dashboard/wallet" element={<Navigate to="/seller/wallet" replace />} />
              <Route path="/dashboard/chats" element={<Navigate to="/seller/chats" replace />} />
              <Route path="/dashboard/tickets" element={<Navigate to="/seller/tickets" replace />} />
              <Route path="/dashboard/tickets/:id" element={<SellerLayout><TicketDetail /></SellerLayout>} />
              <Route path="/dashboard/returns" element={<SellerLayout><DashboardReturns /></SellerLayout>} />
              <Route path="/dashboard/audit-logs" element={<SellerLayout><DashboardAuditLogs /></SellerLayout>} />
              <Route path="/dashboard/managers" element={<SellerLayout><DashboardManagers /></SellerLayout>} />
              
              {/* Shop Stories */}
              <Route path="/dashboard/shop/stories" element={<SellerLayout><ShopStories /></SellerLayout>} />
              <Route path="/dashboard/shop/stories/new" element={<SellerLayout><ShopStoryEditor /></SellerLayout>} />
              <Route path="/dashboard/shop/stories/:id" element={<SellerLayout><ShopStoryEditor /></SellerLayout>} />
              
              {/* Public Shop Stories */}
              <Route path="/company/:companySlug/stories" element={<ShopStoriesList />} />
              <Route path="/company/:companySlug/stories/:postSlug" element={<ShopStoryPage />} />
              
              {/* Import Products */}
              <Route path="/import-products/:companyId" element={<SellerLayout><ImportProducts /></SellerLayout>} />
              
              {/* Admin Routes with Layout */}
              <Route path="/admin" element={<AdminLayout><Admin /></AdminLayout>} />
              <Route path="/admin/users" element={<AdminLayout><AdminUsers /></AdminLayout>} />
              <Route path="/admin/sellers" element={<AdminLayout><AdminSellers /></AdminLayout>} />
              <Route path="/admin/partners" element={<AdminLayout><AdminPartners /></AdminLayout>} />
              <Route path="/admin/analytics" element={<AdminLayout><AdminAnalytics /></AdminLayout>} />
              <Route path="/admin/settings" element={<AdminLayout><AdminSettings /></AdminLayout>} />
              <Route path="/admin/categories" element={<AdminLayout><AdminCategories /></AdminLayout>} />
              <Route path="/admin/regions" element={<AdminLayout><AdminRegions /></AdminLayout>} />
              <Route path="/admin/stories" element={<AdminLayout><AdminStories /></AdminLayout>} />
              <Route path="/admin/scrape-images" element={<AdminLayout><ScrapeProductImages /></AdminLayout>} />
              <Route path="/admin/import-shop" element={<Navigate to="/admin/sellers" replace />} />
              <Route path="/admin/import-shop/:shopId" element={<AdminLayout><PerShopImporter /></AdminLayout>} />
              <Route path="/admin/whatsapp-testing" element={<AdminLayout><AdminWhatsAppTesting /></AdminLayout>} />
              <Route path="/admin/whatsapp-settings" element={<AdminLayout><AdminWhatsAppSettings /></AdminLayout>} />
              <Route path="/admin/travel" element={<AdminLayout><AdminTravel /></AdminLayout>} />
              <Route path="/admin/companies" element={<Navigate to="/admin/sellers" replace />} />
              <Route path="/admin/pending-sellers" element={<Navigate to="/admin/sellers?tab=setup" replace />} />
              <Route path="/admin/crm" element={<Navigate to="/admin/partners" replace />} />
              
              {/* Legal Pages */}
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/shipping-policy" element={<ShippingPolicy />} />
              <Route path="/seller-verification" element={<SellerVerification />} />
              <Route path="/customs-info" element={<CustomsInfo />} />
              <Route path="/returns" element={<Returns />} />
              <Route path="/data-deletion" element={<DataDeletion />} />
              <Route path="/legal/data-protection" element={<DataProtection />} />
              <Route path="/legal/cookies" element={<CookiesPolicy />} />
              <Route path="/about" element={<About />} />
              <Route path="/about-localcraft" element={<AboutLocalCraft />} />
              <Route path="/delete-data" element={<Navigate to="/data-deletion" replace />} />
              <Route path="/data-removal" element={<Navigate to="/data-deletion" replace />} />
              <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
              <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
              
              {/* Checkout & Payment */}
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/payment-status/:orderId" element={<PaymentStatus />} />
              <Route path="/payments/cardcom/success" element={<CardcomSuccess />} />
              <Route path="/payments/cardcom/failure" element={<CardcomFailure />} />
              <Route path="/checkout/cardcom/success" element={<CardComSuccess />} />
              <Route path="/checkout/cardcom/cancel" element={<CardComCancel />} />
              <Route path="/checkout/cardcom/error" element={<CardcomError />} />
              
              {/* Orders */}
              <Route path="/orders" element={<Orders />} />
              <Route path="/orders/:orderId" element={<OrderDetail />} />
              
              {/* Wishlist */}
              <Route path="/wishlist" element={<Wishlist />} />
              <Route path="/wishlist/:userId" element={<Wishlist />} />
              <Route path="/favorites" element={<Navigate to="/wishlist" replace />} />
              
              {/* Contact */}
              <Route path="/contact" element={<Contact />} />
              
              {/* Travel / Stays */}
              <Route path="/stays" element={<StaysHomepage />} />
              <Route path="/stays/search" element={<StaysSearch />} />
              <Route path="/stays/:slug" element={<StayDetails />} />
              
              {/* Admin Travel Routes */}
              <Route path="/admin/travel" element={<AdminLayout><AdminTravel /></AdminLayout>} />
              <Route path="/admin/travel/properties" element={<AdminLayout><AdminTravelProperties /></AdminLayout>} />
              <Route path="/admin/travel/rooms-rates" element={<AdminLayout><AdminTravelRoomsRates /></AdminLayout>} />
              <Route path="/admin/travel/bookings" element={<AdminLayout><AdminTravelBookings /></AdminLayout>} />
              <Route path="/admin/travel/providers" element={<AdminLayout><AdminTravelProviders /></AdminLayout>} />
              
              {/* User Account Routes */}
              <Route path="/account" element={<UserAccountLayout><UserDashboard /></UserAccountLayout>} />
              <Route path="/account/profile" element={<UserAccountLayout><UserProfile /></UserAccountLayout>} />
              <Route path="/account/addresses" element={<UserAccountLayout><UserAddresses /></UserAccountLayout>} />
              <Route path="/account/notifications" element={<UserAccountLayout><UserNotifications /></UserAccountLayout>} />
              <Route path="/account/notification-center" element={<UserAccountLayout><NotificationCenter /></UserAccountLayout>} />
              <Route path="/account/payment-methods" element={<UserAccountLayout><UserPaymentMethods /></UserAccountLayout>} />
              
              {/* Buyer Experience */}
              <Route path="/tickets" element={<Tickets />} />
              <Route path="/tickets/:id" element={<TicketDetail />} />
              <Route path="/support" element={<Support />} />
              <Route path="/support/:ticketId" element={<TicketDetail />} />
              <Route path="/buyer/chats" element={<BuyerChats />} />
              
              {/* Seller Routes */}
              <Route path="/seller/chats" element={<SellerLayout><SellerChats /></SellerLayout>} />
              <Route path="/seller/notifications" element={<SellerLayout><SellerNotifications /></SellerLayout>} />
              <Route path="/seller/wallet" element={<SellerLayout><SellerWallet /></SellerLayout>} />
              <Route path="/seller/financial" element={<SellerLayout><SellerFinancial /></SellerLayout>} />
              <Route path="/seller/crm" element={<SellerLayout><SellerCRM /></SellerLayout>} />
              <Route path="/seller/ai-assistant" element={<SellerLayout><SellerAIAssistant /></SellerLayout>} />
              <Route path="/seller/promos" element={<SellerLayout><SellerPromos /></SellerLayout>} />
              <Route path="/seller/profile" element={<SellerLayout><SellerProfile /></SellerLayout>} />
              <Route path="/seller/analytics" element={<SellerLayout><SellerAnalytics /></SellerLayout>} />
              <Route path="/seller/inventory" element={<SellerLayout><SellerInventory /></SellerLayout>} />
              <Route path="/seller/bundles" element={<SellerLayout><SellerBundles /></SellerLayout>} />
              <Route path="/seller/bundles/new" element={<SellerLayout><SellerBundleForm /></SellerLayout>} />
              <Route path="/seller/bundles/edit/:id" element={<SellerLayout><SellerBundleForm /></SellerLayout>} />
              <Route path="/seller/collaboration-bundles" element={<SellerLayout><SellerCollaborationBundles /></SellerLayout>} />
              <Route path="/seller/collaboration-bundles/new" element={<SellerLayout><SellerCollaborationBundleForm /></SellerLayout>} />
              <Route path="/seller/collaboration-bundles/edit/:id" element={<SellerLayout><SellerCollaborationBundleForm /></SellerLayout>} />
              <Route path="/seller/collaboration-bundles/wizard" element={<SellerLayout><BundleWizard /></SellerLayout>} />
              <Route path="/seller/collaboration-bundles/invitations" element={<SellerLayout><BundleInvitations /></SellerLayout>} />
              <Route path="/seller/collaboration-bundles/invitations/:invitationId" element={<SellerLayout><RespondToInvitation /></SellerLayout>} />
              <Route path="/seller/analytics/bundles" element={<SellerLayout><SellerBundlesAnalyticsPage /></SellerLayout>} />
              <Route path="/seller/bundles/invites" element={<SellerLayout><SellerInvitesPage /></SellerLayout>} />
              <Route path="/seller/bundles/:id/contribute" element={<SellerLayout><ContributeToBundlePage /></SellerLayout>} />
              <Route path="/seller/bundles/:id/offer" element={<SellerLayout><OfferBuilderPage /></SellerLayout>} />
              
              {/* Public Bundles */}
              <Route path="/bundles" element={<BundlesCatalog />} />
              <Route path="/bundles/:slug" element={<PublicCollaborationBundle />} />
              
              {/* Admin Bundles */}
              <Route path="/admin/collaboration-bundles" element={<AdminCollaborationBundles />} />
              <Route path="/admin/collaboration-bundles/:id" element={<AdminCollaborationBundleDetail />} />
              <Route path="/admin/analytics/bundles" element={<AdminLayout><AdminBundlesAnalyticsPage /></AdminLayout>} />
              
              <Route path="/seller/delivery" element={<SellerLayout><DashboardDelivery /></SellerLayout>} />
              <Route path="/seller/tickets" element={<SellerLayout><DashboardTickets /></SellerLayout>} />
              
              {/* More Admin Routes */}
              <Route path="/admin/moderation" element={<AdminLayout><AdminModeration /></AdminLayout>} />
              <Route path="/admin/translations" element={<AdminLayout><AdminTranslations /></AdminLayout>} />
              <Route path="/admin/returns" element={<AdminLayout><AdminReturns /></AdminLayout>} />
              <Route path="/admin/disputes" element={<AdminLayout><AdminDisputes /></AdminLayout>} />
              <Route path="/admin/fraud" element={<AdminLayout><AdminFraud /></AdminLayout>} />
              <Route path="/admin/ocr" element={<AdminLayout><AdminOCR /></AdminLayout>} />
              <Route path="/admin/payouts" element={<AdminLayout><AdminPayouts /></AdminLayout>} />
              <Route path="/admin/financial" element={<AdminLayout><AdminFinancial /></AdminLayout>} />
              <Route path="/admin/finance" element={<Navigate to="/admin/financial" replace />} />
              <Route path="/admin/orders" element={<AdminLayout><AdminOrders /></AdminLayout>} />
              <Route path="/admin/orders/:id" element={<AdminLayout><AdminOrderDetail /></AdminLayout>} />
              <Route path="/admin/products" element={<AdminLayout><AdminProducts /></AdminLayout>} />
              <Route path="/admin/reviews" element={<AdminLayout><AdminReviews /></AdminLayout>} />
              <Route path="/admin/integrations" element={<Navigate to="/admin/settings" replace />} />
              <Route path="/admin/payments-status" element={<AdminLayout><PaymentsStatus /></AdminLayout>} />
              <Route path="/admin/cardcom-settings" element={<AdminLayout><CardComSettings /></AdminLayout>} />
              <Route path="/admin/sellers/:companyId/ratings" element={<AdminLayout><AdminShopRatings /></AdminLayout>} />
              <Route path="/admin/tickets" element={<AdminLayout><AdminTickets /></AdminLayout>} />
              <Route path="/admin/marketing" element={<AdminLayout><AdminMarketing /></AdminLayout>} />
              <Route path="/admin/banners" element={<AdminLayout><AdminBanners /></AdminLayout>} />
              <Route path="/admin/search-shortcuts" element={<AdminLayout><AdminSearchShortcuts /></AdminLayout>} />
              <Route path="/admin/blog" element={<AdminBlog />} />
              <Route path="/admin/security" element={<AdminLayout><AdminSecurity /></AdminLayout>} />
              <Route path="/admin/self-audit" element={<AdminLayout><AdminSelfAudit /></AdminLayout>} />
              <Route path="/admin/audit-logs" element={<AdminLayout><AdminAuditLogs /></AdminLayout>} />
              <Route path="/admin/tickets/:id" element={<AdminLayout><TicketDetail /></AdminLayout>} />
              <Route path="/admin/image-processing" element={<AdminLayout><AdminImageProcessing /></AdminLayout>} />
              <Route path="/admin/collaboration-bundles" element={<AdminLayout><AdminCollaborationBundles /></AdminLayout>} />
              <Route path="/admin/collaboration-bundles/:id" element={<AdminLayout><AdminCollaborationBundleDetail /></AdminLayout>} />
              <Route path="/admin/bundles-analytics" element={<AdminLayout><AdminBundlesAnalyticsPage /></AdminLayout>} />
              
              {/* Misc */}
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/privacy-center" element={<PrivacyCenter />} />
              <Route path="/offline" element={<Offline />} />
              <Route path="/install" element={<Install />} />
              
              {/* Internal SEO Tools */}
              <Route path="/internal/seo/domain-check" element={<SeoDomainCheck />} />
              
              {/* 404 */}
              <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              </ChunkErrorBoundary>
            
            <BottomNavBar />
        </BrowserRouter>
      </TooltipProvider>
    </ProfileProvider>
  </QueryClientProvider>
</HelmetProvider>
);

export default App;
